import React, { useState, useEffect } from 'react';
import { Box, Button } from '@mui/material';
import { useParams, useRouteLoaderData, useSearchParams } from 'react-router-dom';
import SyncWizard from "components/SyncWizard";
import SyncWizardFooterWrapper from "components/SyncWizardFooterWrapper";
import ListCutterJobReviewAndSubmit from "./steps/ListCutterJobReviewAndSubmit";
import ListCutterJobSelectColumns from "./steps/ListCutterJobSelectColumns";
import ListCutterJobSelectData from "./steps/ListCutterJobSelectData";
import ListCutterJobSelectFilters from "./steps/ListCutterJobSelectFilters";
import buildRoute from 'components/buildRoute';
import useAsync from "hooks/useAsync";

const NewListCutterJob = () => {
  const { activeProjectDetails } = useRouteLoaderData("projectRoot");
  const { project } = activeProjectDetails;
  const { targetsmart_matching: targetsmartMatchingEnabled, catalist_matching: catalistMatchingEnabled } = project;
  
  const { projectId } = useParams();
  const [activeStep, setActiveStep] = useState(0);
  const [sourceDataset, setSourceDataset] = useState(null);
  const [sourceTable, setSourceTable] = useState(null);
  const [availableColumns, setAvailableColumns] = useState([]);
  const [columnsWithFilters, setColumnsWithFilters] = useState([]);
  const [finalSelectedColumns, setFinalSelectedColumns] = useState([]);
  const [targetDataset, setTargetDataset] = useState('');
  const [targetTable, setTargetTable] = useState('');
  const [sourceVoterfileType, setsourceVoterfileType] = useState(null);
  const [sourceTableSchema, setSourceTableSchema] = useState([]);
  const [searchParams] = useSearchParams();
  const templateId = searchParams.get('template');
  const [orderByConditions, setOrderByConditions] = useState([]);
  const [hasLimit, setHasLimit] = useState(false);
  const [limitValue, setLimitValue] = useState(null);
  const [validationError, setValidationError] = useState(false);

  const handleNext = () => {
    // Prevent advancing if there's a validation error
    if (validationError) {
      return;
    }

    // Step 1: Validate source dataset and table
    if (activeStep === 0) {
      if (!sourceDataset || !sourceTable) {
        return;
      }
    }
    
    // Step 2: Validate filters and limit
    if (activeStep === 1) {
      const hasInvalidFilters = Array.isArray(columnsWithFilters) && columnsWithFilters.some(col => 
        col.column && (!col.filter || col.filter.trim() === '')
      );
      
      // Updated limit validation - if limit is enabled, must have a valid numeric value
      const hasInvalidLimit = hasLimit && (
        limitValue === null || 
        limitValue === '' || 
        isNaN(limitValue) || 
        parseInt(limitValue) <= 0
      );
      
      if (hasInvalidFilters || hasInvalidLimit) {
        return;
      }
    }
    
    // Step 3: Validate selected columns
    if (activeStep === 2) {
      if (!Array.isArray(finalSelectedColumns) || finalSelectedColumns.length === 0) {
        return;
      }
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSelectDataset = (event, option) => {
    setSourceTable(null);
    setSourceDataset(option ?? null);
  };

  const handleSelectTable = (event, option) => {
    setSourceTable(option ?? null);
  };

  const handleSelectDataSourceType = (type) => {
    setsourceVoterfileType(type);
  };

  const requestToFetchAvailableColumns = useAsync(async () => {
    if (!sourceTable) {
      return [];
    }

    const queryParams = new URLSearchParams();
    queryParams.set("project_id", projectId);
    queryParams.set("dataset_id", sourceDataset);
    queryParams.set("table_id", sourceTable);
    const response = await fetch(`/bigquery/get-table-schema/?${queryParams.toString()}`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const table_schema = await response.json();

    const column_names = table_schema.map(({ name }) => name);
    // exclude any columns beginning with an underscore
    const filtered_column_names = column_names.filter((column_name) => !column_name.startsWith("_"));
    setAvailableColumns(filtered_column_names);
    setSourceTableSchema(table_schema);
    return filtered_column_names;
  });

  useEffect(() => {
    const loadTemplate = async () => {
      if (templateId) {
        try {
          const response = await fetch(`/list-cutter/api/jobs/${templateId}/`);
          if (!response.ok) {
            throw new Error('Failed to load template job');
          }
          const templateJob = await response.json();
          
          // First set the source information
          setSourceDataset(templateJob.source_dataset);
          setSourceTable(templateJob.source_table);
          setsourceVoterfileType(templateJob.source_voterfile_type);
          
          // Set target information
          setTargetDataset(templateJob.target_dataset);
          // Generate new target table name with timestamp
          const timestamp = new Date().getTime();
          setTargetTable(`${templateJob.target_table}_copy_${timestamp}`);

          // Store template data to apply after columns are loaded
          window.sessionStorage.setItem('templateData', JSON.stringify({
            filters: templateJob.filters,
            selectedColumns: templateJob.selected_columns,
            orderByConditions: templateJob.order_by_conditions,
            hasLimit: templateJob.has_limit,
            limitValue: templateJob.limit
          }));
          
        } catch (error) {
          console.error('Error loading template:', error);
          // Maybe show an error message to the user
        }
      }
    };

    loadTemplate();
  }, [templateId]);

  // Add a new effect to handle setting filters and columns after available columns are loaded
  useEffect(() => {
    const applyTemplateData = async () => {
      const templateDataString = window.sessionStorage.getItem('templateData');
      if (templateDataString && availableColumns.length > 0) {
        const templateData = JSON.parse(templateDataString);
        
        // Set the filters and columns
        setColumnsWithFilters(templateData.filters);
        setFinalSelectedColumns(templateData.selectedColumns);
        
        // Add these lines to set order by and limit
        setOrderByConditions(templateData.orderByConditions || []);
        setLimitValue(templateData.limitValue || null);
        
        // Clear the temporary storage
        window.sessionStorage.removeItem('templateData');
        
        // Now we can safely skip to the review step
        setActiveStep(3);
      }
    };

    applyTemplateData();
  }, [availableColumns]);

  // Modify the existing useEffect for fetching columns to include source dataset
  useEffect(() => {
    if (sourceDataset && sourceTable) {  // Check for both values
      requestToFetchAvailableColumns.perform();
    }
  }, [sourceDataset, sourceTable]);  // Depend on both values

  const handleFilterStepComplete = (
    filters, 
    targetDatasetId, 
    targetTableId, 
    orderBy,
    hasLimit,
    limit
  ) => {
    setColumnsWithFilters(filters);
    setTargetDataset(targetDatasetId);
    setTargetTable(targetTableId);
    setOrderByConditions(orderBy);
    setHasLimit(hasLimit);
    setLimitValue(limit);
    
    // Set validation error to true if no dataset is selected
    setValidationError(!targetDatasetId);
  };

  const handleValidationErrorChange = (hasErrors) => {
    setValidationError(hasErrors);
  };

  const canNavigateToStep = (stepIndex) => {
    // Can always go back
    if (stepIndex < activeStep) return true;

    // Check requirements for each step
    switch (stepIndex) {
      case 0:
        return true;  // Can always go to first step
      case 1:
        return sourceDataset && sourceTable;  // Need source data selected
      case 2:
        // Check for invalid filters
        const hasInvalidFilters = Array.isArray(columnsWithFilters) && 
          columnsWithFilters.some(col => col.column && (!col.filter || col.filter.trim() === ''));
        
        // Check for valid limit when limit is enabled
        const hasInvalidLimit = hasLimit && (
          limitValue === null || 
          limitValue === '' || 
          isNaN(limitValue) || 
          parseInt(limitValue) <= 0
        );

        return sourceDataset && sourceTable && 
               !hasInvalidFilters &&
               hasInvalidLimit;
      case 3:
        // Add defensive check for both arrays
        return sourceDataset && sourceTable && 
               (!Array.isArray(columnsWithFilters) || !columnsWithFilters.some(col => 
                 col.column && !col.filter
               )) &&
               Array.isArray(finalSelectedColumns) && finalSelectedColumns.length > 0;
      default:
        return false;
    }
  };

  const handleStepClick = (stepIndex) => {
    if (canNavigateToStep(stepIndex)) {
      setActiveStep(stepIndex);
    }
  };

  // AFTER all function definitions, define the step components
  const Step1_SelectData = (
    <>
      <ListCutterJobSelectData
        projectId={projectId}
        sourceDataset={sourceDataset}
        onSelectDataset={handleSelectDataset}
        sourceTable={sourceTable}
        onSelectTable={handleSelectTable}
        sourceVoterfileType={sourceVoterfileType}
        onSelectDataSourceType={handleSelectDataSourceType}
        isCatalistEnabled={catalistMatchingEnabled}
        isTargetsmartEnabled={targetsmartMatchingEnabled}
      />
      <SyncWizardFooterWrapper sxOverrides={{ justifyContent: "end" }}>
        <Button variant="outlined" onClick={handleNext}>
          Next
        </Button>
      </SyncWizardFooterWrapper>
    </>
  );

  const Step2_SelectFilters = (
    <>
      <ListCutterJobSelectFilters
        availableColumns={availableColumns}
        selectedColumnFilters={columnsWithFilters}
        onColumnsChange={setColumnsWithFilters}
        onTargetDetailsChange={handleFilterStepComplete}
        onValidationErrorChange={handleValidationErrorChange}
        sourceDataset={sourceDataset}
        sourceTable={sourceTable}
        sourceTableSchema={sourceTableSchema}
        projectId={projectId}
        initialOrderByConditions={orderByConditions}
        initialLimitValue={limitValue}
        initialTargetDataset={targetDataset}
        initialTargetTable={targetTable}
        limitError={hasLimit && (
          limitValue === null || limitValue === '' ? 
            "Please enter a number" : 
          !isNaN(limitValue) && parseInt(limitValue) <= 0 ? 
            "Number must be greater than 0" :
          isNaN(limitValue) ? 
            "Please enter a valid number" : 
            ""
        )}
      />
      <SyncWizardFooterWrapper sxOverrides={{ justifyContent: "space-between" }}>
        <Button color="inherit" onClick={handleBack}>
          Back
        </Button>
        <Button 
          variant="contained" 
          onClick={handleNext} 
          disabled={validationError}
        >
          Next
        </Button>
      </SyncWizardFooterWrapper>
    </>
  );

  const Step3_SelectColumns = (
    <>
      <ListCutterJobSelectColumns
        availableColumns={availableColumns}
        selectedColumns={finalSelectedColumns}
        onSelectedColumnsChange={setFinalSelectedColumns}
      />
      <SyncWizardFooterWrapper sxOverrides={{ justifyContent: "space-between" }}>
        <Button color="inherit" onClick={handleBack}>
          Back
        </Button>
        <Button variant="contained" onClick={handleNext}>
          Next
        </Button>
      </SyncWizardFooterWrapper>
    </>
  );

  const Step4_ReviewAndSubmit = (
    <>
      <ListCutterJobReviewAndSubmit
        projectId={projectId}
        sourceDataset={sourceDataset}
        sourceTable={sourceTable}
        sourceVoterfileType={sourceVoterfileType}
        selectedColumns={finalSelectedColumns}
        columnsWithFilters={columnsWithFilters}
        orderByConditions={orderByConditions}
        hasLimit={hasLimit}
        limitValue={limitValue}
        initialTargetDataset={targetDataset}
        initialTargetTable={targetTable}
        onNavigateToStep={handleStepClick}
      />
      <SyncWizardFooterWrapper sxOverrides={{ justifyContent: "space-between" }}>
        <Button color="inherit" onClick={handleBack}>
          Back
        </Button>
        <Button variant="contained" onClick={() => {
          window.location.href = `/projects/${projectId}/list-cutter`;
        }}>
          View Jobs
        </Button>
      </SyncWizardFooterWrapper>
    </>
  );

  const steps = [
    {
      label: "Select Data Source",
      content: Step1_SelectData
    },
    {
      label: "Set Filters",
      content: Step2_SelectFilters
    },
    {
      label: "Select Columns",
      content: Step3_SelectColumns
    },
    {
      label: "Confirm and Submit",
      content: Step4_ReviewAndSubmit
    }
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <SyncWizard
        steps={steps}
        activeStepIndex={activeStep}
        onStepClick={handleStepClick}
        loadingMessage={"Loading..."}
        loadingBackdropTestId={"list-cutter-job-backdrop"}
        disableStepClick={validationError}
      />
    </Box>
  );
};

const routeArgs = {
  element: <NewListCutterJob />,
};

export default buildRoute(routeArgs); 